import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { S, formatCHF } from '../regureStyles';
import { PAYMENT_FREQUENCY_LABEL_KEYS } from '../../utils/productCatalog';
import { frequencyControlState, previewPaymentSchedule } from '../../utils/paymentTerms';

// ─── One payment-frequency control for RC Pro, PGMA and PGM ────────────
//
// The QA pass on 2026-09-28 found three behaviours for one rule: RC Pro hid
// the control, PGM showed it greyed out with no reason, and PGMA offered
// Trimestriel and Mensuel on a CHF 1'044 premium with no surcharge. Every
// form now renders this, and what it offers comes only from the tenant's
// paymentTerms for the product (utils/paymentTerms.js):
//
//   not configured          Annuel, disabled, "not available for this product"
//   premium ≤ threshold     Annuel, disabled, "available from CHF {threshold}"
//   premium > threshold     the configured frequencies, with the surcharge,
//                           the instalment amount and the total shown below
//
// The server applies the same predicates and stores the schedule; this is
// the preview the operator sees before creating the quote.

const selectStyle = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S.borderLight}`, borderRadius: 8,
  fontSize: 13, color: S.textPrimary, background: '#fff', outline: 'none', fontFamily: 'inherit',
};

// Rendered INSIDE each form's own <Field> so the label matches the form.
export default function PaymentFrequencyField({ terms, premium, value, onChange, currency }) {
  const { t } = useTranslation();
  const { options, disabled, hint } = frequencyControlState(terms, premium);
  const selected = options.includes(value) ? value : options[0];
  const schedule = selected !== 'annual' ? previewPaymentSchedule(premium, terms, selected) : null;

  // What is SHOWN is what is SENT. A quarterly choice made above the threshold
  // must not survive the premium falling below it: the control would show
  // Annuel while the form still posted quarterly, and the server would refuse
  // the quote with no visible reason.
  useEffect(() => {
    if (value && value !== selected) onChange(selected);
  }, [value, selected, onChange]);

  return (
    <div data-testid='payment-frequency'>
      <select
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-describedby={hint ? 'payment-frequency-hint' : undefined}
        style={{ ...selectStyle, cursor: disabled ? 'not-allowed' : 'pointer', background: disabled ? '#f8fafc' : '#fff' }}
      >
        {options.map((code) => (
          <option key={code} value={code}>{t(PAYMENT_FREQUENCY_LABEL_KEYS[code] || 'offers.pgm.paymentAnnual')}</option>
        ))}
      </select>

      {/* The threshold is a rule, not a price: printed as the tenant wrote it,
          "CHF 10'000", without minor units. */}
      {hint && (
        <div id='payment-frequency-hint' style={{ fontSize: 12, color: S.textMuted, marginTop: 6 }}>
          {hint.key === 'belowThreshold'
            ? t('offers.payment.belowThreshold', { amount: formatCHF(hint.amount, currency).replace(/[.,]00$/, '') })
            : t('offers.payment.notConfigured')}
        </div>
      )}

      {schedule && (
        <div data-testid='payment-schedule' style={{ marginTop: 8, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, fontSize: 12, color: S.textSecondary, lineHeight: 1.7 }}>
          <div>
            {schedule.rate != null
              ? t('offers.payment.surchargeRate', { rate: schedule.rate, amount: formatCHF(schedule.surcharge, currency) })
              : t('offers.payment.surcharge', { amount: formatCHF(schedule.surcharge, currency) })}
          </div>
          <div>{t('offers.payment.instalments', { count: schedule.instalments, amount: formatCHF(schedule.perInstalment, currency) })}</div>
          <div style={{ fontWeight: 700, color: S.textPrimary }}>{t('offers.payment.totalPayable', { amount: formatCHF(schedule.total, currency) })}</div>
        </div>
      )}
    </div>
  );
}
